import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';

const TONALITY_OPTIONS = [
  'Human and conversational',
  'Direct and confident',
  'Casual Australian',
  'Professional but warm',
];

const SERVICES = [
  'Google Review Agent',
  'Website Development',
  'Chatbot (Carl)',
  'Automated Admin Assistant',
];

const DEFAULT_SETTINGS = {
  openrouterApiKey: '',
  appsScriptUrl: '',
  senderName: 'Zac @ ZJ Marketing',
  dailyEmailCap: 50,
  followUpDelayDays: 5,
  maxFollowUps: 2,
  requireApproval: true,
  tonality: 'Human and conversational',
  emailScript: api.DEFAULT_EMAIL_SCRIPT || '',
  callScriptGoogleReview: '',
  callScriptWebsite: '',
  callScriptChatbot: '',
  callScriptAdmin: '',
};

export default function Settings() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    api.getSettings()
      .then(data => setSettings(prev => ({ ...DEFAULT_SETTINGS, ...data })))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaved(false);
    setError('');
    try {
      await api.saveSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) return <div className="text-gray-400">Loading settings...</div>;

  const inputClass = 'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500';
  const textareaClass = 'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 font-mono';
  const labelClass = 'block text-sm font-medium text-gray-300 mb-1';
  const helpClass = 'text-xs text-gray-500 mt-1';

  const tabs = [
    { key: 'general', label: 'General' },
    { key: 'email', label: 'Email' },
    { key: 'scripts', label: 'Scripts' },
    { key: 'services', label: 'Services' },
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Settings</h2>

      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-sm text-red-400">{error}</div>}
      {saved && <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4 text-sm text-green-400">Settings saved!</div>}

      {/* CRM Link */}
      <div className="mb-6">
        <a
          href="https://supabase.com/dashboard/project/jzgvjqltgduxctefdrxp/editor"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 bg-emerald-600/20 border border-emerald-600/30 text-emerald-400 px-4 py-2 rounded-lg text-sm hover:bg-emerald-600/30 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582 4 8 4s8 1.79 8 4" />
          </svg>
          View CRM (Supabase)
        </a>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-900 rounded-lg p-1 w-fit">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === t.key ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-6 max-w-3xl">
        {/* General Tab */}
        {activeTab === 'general' && (
          <>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h3 className="text-lg font-semibold mb-4">AI Configuration</h3>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>OpenRouter API Key</label>
                  <input
                    type="password"
                    value={settings.openrouterApiKey || ''}
                    onChange={e => setSettings({ ...settings, openrouterApiKey: e.target.value })}
                    className={inputClass}
                  />
                  <p className={helpClass}>Get your free key at openrouter.ai/keys</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h3 className="text-lg font-semibold mb-4">Email Configuration</h3>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Apps Script URL</label>
                  <input
                    type="text"
                    value={settings.appsScriptUrl || ''}
                    onChange={e => setSettings({ ...settings, appsScriptUrl: e.target.value })}
                    className={inputClass}
                  />
                  <p className={helpClass}>Deploy your Apps Script as a Web App and paste the URL here</p>
                </div>
                <div>
                  <label className={labelClass}>Sender Display Name</label>
                  <input
                    type="text"
                    value={settings.senderName || ''}
                    onChange={e => setSettings({ ...settings, senderName: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h3 className="text-lg font-semibold mb-4">Outreach Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Daily Email Cap</label>
                  <input
                    type="number"
                    value={settings.dailyEmailCap || 50}
                    onChange={e => setSettings({ ...settings, dailyEmailCap: parseInt(e.target.value) || 50 })}
                    className={inputClass}
                  />
                  <p className={helpClass}>Maximum emails to send per day</p>
                </div>
                <div>
                  <label className={labelClass}>Follow Up Delay (days)</label>
                  <input
                    type="number"
                    value={settings.followUpDelayDays || 5}
                    onChange={e => setSettings({ ...settings, followUpDelayDays: parseInt(e.target.value) || 5 })}
                    className={inputClass}
                  />
                  <p className={helpClass}>Days to wait before follow up</p>
                </div>
                <div>
                  <label className={labelClass}>Max Follow Ups</label>
                  <input
                    type="number"
                    value={settings.maxFollowUps || 2}
                    onChange={e => setSettings({ ...settings, maxFollowUps: parseInt(e.target.value) || 2 })}
                    className={inputClass}
                  />
                  <p className={helpClass}>After this many follow ups, mark as Cold</p>
                </div>
                <div>
                  <label className={labelClass}>Require Approval Before Sending</label>
                  <button
                    onClick={() => setSettings({ ...settings, requireApproval: !settings.requireApproval })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.requireApproval ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.requireApproval ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                  <p className={helpClass}>Show draft for review before sending</p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Email Tab */}
        {activeTab === 'email' && (
          <>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h3 className="text-lg font-semibold mb-4">Email Tonality</h3>
              <div className="space-y-2">
                {TONALITY_OPTIONS.map(opt => (
                  <label key={opt} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-gray-800 transition-colors">
                    <input
                      type="radio"
                      name="tonality"
                      value={opt}
                      checked={settings.tonality === opt}
                      onChange={() => setSettings({ ...settings, tonality: opt })}
                      className="text-blue-500"
                    />
                    <span className="text-sm text-gray-200">{opt}</span>
                  </label>
                ))}
              </div>
              <p className={`${helpClass} mt-3`}>Controls how AI-generated emails sound. Applied to every email.</p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h3 className="text-lg font-semibold mb-2">Email Script Framework</h3>
              <p className="text-xs text-gray-500 mb-3">This framework is given to the AI as instructions for structuring emails. Edit to change the formula.</p>
              <textarea
                value={settings.emailScript || ''}
                onChange={e => setSettings({ ...settings, emailScript: e.target.value })}
                rows={8}
                className={textareaClass}
                placeholder="Enter email framework instructions..."
              />
            </div>
          </>
        )}

        {/* Scripts Tab */}
        {activeTab === 'scripts' && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-4">Call Scripts</h3>
            <p className="text-xs text-gray-500 mb-5">One script per service. Used by setters when following up by phone.</p>
            <div className="space-y-5">
              {[
                { key: 'callScriptGoogleReview', label: 'Google Review Agent' },
                { key: 'callScriptWebsite', label: 'Website Development' },
                { key: 'callScriptChatbot', label: 'Chatbot (Carl)' },
                { key: 'callScriptAdmin', label: 'Automated Admin Assistant' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className={labelClass}>{label}</label>
                  <textarea
                    value={settings[key] || ''}
                    onChange={e => setSettings({ ...settings, [key]: e.target.value })}
                    rows={3}
                    className={textareaClass}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-2">Service Descriptions</h3>
            <p className="text-xs text-gray-500 mb-4">These descriptions are used in AI email generation. Edit to refine how each service is explained.</p>
            <div className="space-y-5">
              {SERVICES.map(s => (
                <div key={s}>
                  <label className={labelClass}>{s}</label>
                  <p className="text-xs text-gray-600 mb-1.5">{api.SERVICE_DESCRIPTIONS[s]}</p>
                  <p className="text-xs text-gray-500 italic">(Service descriptions are managed in code. Contact developer to update.)</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={handleSave}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 rounded-lg font-medium transition-colors"
        >
          Save Settings
        </button>
      </div>
    </div>
  );
}
